<template>
  <div class="fuelling-usage">
    <div class="usage-header">
      <div>
        <h3>Fuelling usage</h3>
        <p class="usage-sub">Are players actually opening the guidance?</p>
      </div>
      <select v-model.number="days" class="days-select" @change="load">
        <option :value="7">Last 7 days</option>
        <option :value="30">Last 30 days</option>
        <option :value="90">Last 90 days</option>
      </select>
    </div>

    <div v-if="loading" class="usage-loading">Loading usage…</div>
    <div v-else-if="error" class="usage-error">{{ error }}</div>

    <template v-else-if="usage">
      <!-- Summary -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-value">{{ usage.summary.active_players }}/{{ usage.summary.total_players }}</div>
          <div class="summary-label">Players using it</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ usage.summary.total_opens }}</div>
          <div class="summary-label">Total opens</div>
        </div>
        <div class="summary-card">
          <div class="summary-value">{{ engagementPercent }}%</div>
          <div class="summary-label">Engagement</div>
        </div>
      </div>

      <!-- Pathway breakdown -->
      <div v-if="usage.pathways.length" class="usage-section">
        <h4>Most-opened pathways</h4>
        <div v-for="p in usage.pathways" :key="p.event_key" class="pathway-row">
          <span class="pathway-name">{{ pathwayLabel(p.event_key) }}</span>
          <div class="pathway-bar-track">
            <div class="pathway-bar" :style="{ width: pathwayWidth(p.opens) }"></div>
          </div>
          <span class="pathway-count">{{ p.opens }}</span>
        </div>
      </div>

      <!-- Per-player -->
      <div class="usage-section">
        <h4>By player</h4>
        <table class="usage-table">
          <thead>
            <tr><th>Player</th><th>Opens</th><th>Active days</th><th>Last used</th></tr>
          </thead>
          <tbody>
            <tr v-for="pl in usage.players" :key="pl.id" :class="{ inactive: Number(pl.opens) === 0 }">
              <td>{{ pl.name }}</td>
              <td>{{ pl.opens }}</td>
              <td>{{ pl.active_days }}</td>
              <td>{{ formatLastUsed(pl.last_used) }}</td>
            </tr>
          </tbody>
        </table>
        <p class="usage-hint">Players with no opens are highlighted — worth a nudge.</p>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";

// Maps the pathway event_key logged by the app to a readable label.
const PATHWAY_LABELS = {
  training_evening: "Training tonight",
  match_early: "Match — 10:30am",
  match_afternoon: "Match — 1pm",
  match_evening: "Match — 7:30pm",
  recovery: "Just finished",
  travel: "Travelling / away day",
  rest_day: "Rest day",
};

export default {
  name: "FuellingUsage",

  data() {
    return {
      loading: true,
      error: null,
      days: 30,
      usage: null,
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    clubId() {
      return this.getProfile?.clubId;
    },

    engagementPercent() {
      const total = this.usage?.summary?.total_players || 0;
      const active = this.usage?.summary?.active_players || 0;
      if (!total) return 0;
      return Math.round((active / total) * 100);
    },

    maxPathwayOpens() {
      if (!this.usage?.pathways?.length) return 0;
      return Math.max(...this.usage.pathways.map((p) => Number(p.opens) || 0));
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await Nutrition.getNutritionUsage(this.clubId, null, this.days);
        if (res.data && res.data.success) {
          this.usage = res.data.data;
        } else {
          this.error = (res.data && res.data.error) || "Failed to load usage";
        }
      } catch (e) {
        this.error = "Failed to load usage";
      } finally {
        this.loading = false;
      }
    },

    pathwayLabel(key) {
      return PATHWAY_LABELS[key] || key;
    },

    pathwayWidth(opens) {
      if (!this.maxPathwayOpens) return "0%";
      return Math.round((Number(opens) / this.maxPathwayOpens) * 100) + "%";
    },

    formatLastUsed(value) {
      if (!value) return "—";
      const d = new Date(value.replace(" ", "T"));
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.fuelling-usage {
  padding: 4px 0;
}
.usage-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}
.usage-header h3 {
  margin: 0;
}
.usage-sub {
  margin: 2px 0 0;
  color: #6b7280;
  font-size: 0.85rem;
}
.days-select {
  padding: 6px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;
}
.usage-loading,
.usage-error {
  padding: 24px;
  text-align: center;
  color: #6b7280;
}
.usage-error {
  color: #b91c1c;
}
.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}
.summary-card {
  background: #f9fafb;
  border: 1px solid #eee;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
}
.summary-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #111827;
}
.summary-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 4px;
}
.usage-section {
  margin-bottom: 24px;
}
.usage-section h4 {
  margin: 0 0 10px;
}
.pathway-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.pathway-name {
  flex: 0 0 150px;
  font-size: 0.85rem;
}
.pathway-bar-track {
  flex: 1;
  height: 10px;
  background: #f1f1f1;
  border-radius: 6px;
  overflow: hidden;
}
.pathway-bar {
  height: 100%;
  background: #1abc9c;
  border-radius: 6px;
}
.pathway-count {
  flex: 0 0 32px;
  text-align: right;
  font-size: 0.85rem;
  font-weight: 600;
}
.usage-table {
  width: 100%;
  border-collapse: collapse;
}
.usage-table th,
.usage-table td {
  text-align: left;
  padding: 8px 10px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 0.9rem;
}
.usage-table th {
  color: #6b7280;
  font-weight: 600;
}
.usage-table tr.inactive {
  background: #fff7ed;
}
.usage-hint {
  margin-top: 8px;
  font-size: 0.8rem;
  color: #9ca3af;
}
</style>
